/**
 * The quick buttons that fold a hierarchy by band: "Show down to L0 · L1 · … · All".
 *
 * Issue #93 asked for exactly this: collapse by requirement level, "or similar
 * for parts". Shared by both hierarchies, so a band is a level in one and a
 * containment depth in the other, and the labels come from the caller.
 *
 * Choosing a band clears every box's own fold setting. A cutoff is a statement
 * about the whole diagram, and keeping the boxes opened by hand under the old
 * one would make the new one mean something different on every box.
 */

import Stack from '@mui/material/Stack'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'
import type { Folding } from './focus'

/** The value the "All" button carries; a ToggleButton cannot carry null. */
const ALL = 'all'

export default function FoldBar({
  bands,
  folding,
  onChange,
  shown,
  total,
  noun,
}: {
  /** Every band the unfolded diagram has, in order, with its label. */
  bands: { band: number; label: string }[]
  folding: Folding
  onChange: (next: Folding) => void
  /** Boxes drawn after folding, and before - for the "12 of 40" line. */
  shown: number
  total: number
  /** What a box is, plural: "requirements", "parts". */
  noun: string
}) {
  // A one-band diagram has nothing to fold; the deepest band is the same as
  // "All", so it gets no button of its own.
  const choices = bands.slice(0, -1)
  const value = folding.cutoff === null ? ALL : String(folding.cutoff)
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 }}>
      {choices.length > 0 && (
        <>
          <Typography variant="caption" color="text.secondary">
            Show down to
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={value}
            aria-label="Show down to"
            onChange={(_event, next: string | null) => {
              // Clicking the selected button again would deselect it; that is
              // not a fold anybody asked for, so it does nothing.
              if (next === null) return
              onChange({ cutoff: next === ALL ? null : Number(next), open: new Map() })
            }}
          >
            {choices.map((band) => (
              <ToggleButton key={band.band} value={String(band.band)} sx={{ py: 0.25 }}>
                {band.label}
              </ToggleButton>
            ))}
            <ToggleButton value={ALL} sx={{ py: 0.25 }}>
              All
            </ToggleButton>
          </ToggleButtonGroup>
        </>
      )}
      <Typography variant="caption" color="text.secondary">
        {shown === total
          ? `Showing all ${total} ${noun}.`
          : `Showing ${shown} of ${total} ${noun}; a box’s +N opens what is folded under it.`}
      </Typography>
    </Stack>
  )
}
